import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { ManagementApi } from '../api/client';
import { Drawer } from '../components/Drawer';
import { RefreshActionButton } from '../components/RefreshActionButton';
import { defaultDirtyDraftGuardMessage, useDirtyDraftGuard } from '../configEditor';
import { PropertyTable, ResourceHeader, StatusStrip, Tabs } from '../components/ResourceDetail';
import { useI18n, useVisibleCopy } from '../i18n';
import { isRealPersonaPlatform } from '../lib/platforms';
import { redactSensitiveText } from '../lib/redaction';
import { createPromptControlPlaneCopy, type PromptControlPlaneCopy } from './copy';
import {
  buildPromptArtifactCreatePreview,
  buildPromptArtifactLifecyclePreview,
  buildPromptAssignmentMutationPreview,
  buildPromptAssignmentModel,
  buildPromptControlPlaneModel,
  buildPromptDraftJsonDrawerModel,
  buildPromptModuleActionControl,
  buildPromptModuleDetailModel,
  buildResetPlatformAssignmentPreview,
  type PromptArtifactCreateInput,
  type PromptArtifactLifecycleKind,
  type PromptAssignmentModel,
  type PromptAssignmentSources,
  type PromptConditionsForm,
  type PromptControlPlaneModel,
  type PromptControlPlaneSources,
  type PromptEvidenceCandidate,
  type PromptModuleActionKind,
  type PromptModuleDetailModel,
  type PromptModuleDetailSources,
  type PromptModuleRow,
  type PromptMutationPreview,
} from './model';

type ListState = {
  loading: boolean;
  sources: PromptControlPlaneSources;
  model: PromptControlPlaneModel;
  errors: Record<string, string>;
};

type DetailState = {
  loading: boolean;
  sources: PromptModuleDetailSources;
  model: PromptModuleDetailModel;
  errors: Record<string, string>;
  actionMessage: string;
};

type AssignmentState = {
  loading: boolean;
  sources: PromptAssignmentSources;
  model: PromptAssignmentModel;
  errors: Record<string, string>;
};

type DetailTab = 'overview' | 'evidence' | 'history';
type AssignmentTab = 'overview' | 'modules' | 'edit' | 'raw';
type DetailAction = PromptModuleActionKind | null;
type ListDrawer = 'create' | null;
type DetailDrawer = 'edit' | 'draft_json' | 'config_json' | PromptModuleActionKind | PromptArtifactLifecycleKind | null;
type DetailConfirmation = { action: DetailAction; confirmationKey: string };
type CreateFormState = Required<Pick<PromptArtifactCreateInput, 'name' | 'displayName' | 'description' | 'markdown'>> & { priority: number; conditions: PromptConditionsForm };
type PromptEditorFormState = {
  displayName: string;
  description: string;
  markdown: string;
  priority: number;
  conditions: PromptConditionsForm;
  status: string;
  classification: PromptModuleRow['classification'];
  scope: string;
  overrideTarget: string;
};
type LifecycleConfirmation = { kind: PromptArtifactLifecycleKind | null; confirmationKey: string };
type PromptListFilters = {
  search: string;
  classification: PromptModuleRow['classification'] | 'all';
  state: 'all' | 'active' | 'inactive' | 'mutable' | 'readonly' | 'override_allowed';
};

type PromptModuleGroupId = 'core' | 'builtin' | 'console';
type PromptModuleGroup = { id: PromptModuleGroupId; modules: PromptModuleRow[] };
type PromptModuleDisplayGroup = PromptModuleGroup & { title: string; description: string };
type PromptModuleSharedMetadataItem = { label: string; value: string };

const emptyDetailConfirmation: DetailConfirmation = { action: null, confirmationKey: '' };
const emptyLifecycleConfirmation: LifecycleConfirmation = { kind: null, confirmationKey: '' };
const emptyPromptListFilters: PromptListFilters = { search: '', classification: 'all', state: 'all' };
const emptyPromptConditions: PromptConditionsForm = { platforms: [], agents: [] };
const emptyPromptEditorForm: PromptEditorFormState = {
  displayName: '',
  description: '',
  markdown: '',
  priority: 10,
  conditions: emptyPromptConditions,
  status: '',
  classification: 'managed',
  scope: 'project',
  overrideTarget: '',
};
const promptConditionPlatformOptions = ['linear', 'lark', 'slack', 'discord', 'telegram'];

export function PromptControlPlanePage() {
  const { t } = useI18n();
  const copy = useMemo(() => createPromptControlPlaneCopy(t), [t]);
  const api = useMemo(() => new ManagementApi(), []);
  const [drawer, setDrawer] = useState<ListDrawer>(null);
  const [filters, setFilters] = useState<PromptListFilters>(emptyPromptListFilters);
  const [createForm, setCreateForm] = useState<CreateFormState>({ name: '', displayName: '', description: '', markdown: '# New prompt\n', priority: 10, conditions: emptyPromptConditions });
  const [createConfirmedKey, setCreateConfirmedKey] = useState('');
  const [createMessage, setCreateMessage] = useState('');
  const [state, setState] = useState<ListState>(() => {
    const sources = {};
    return { loading: true, sources, model: buildPromptControlPlaneModel(sources, copy), errors: {} };
  });

  const load = useCallback(async () => {
    const errors: Record<string, string> = {};
    setState(previous => ({ ...previous, loading: true }));
    const moduleList = await capture(copy.list.modulesTitle, api.getPromptModules(), errors);
    const sources = { moduleList, errors };
    setState({ loading: false, sources, model: buildPromptControlPlaneModel(sources, copy), errors });
  }, [api, copy]);

  useEffect(() => {
    void load();
  }, [load]);

  const { model } = state;
  const createPreview = buildPromptArtifactCreatePreview(createForm, copy);
  const visibleModules = useMemo(() => filterPromptModules(model.modules, filters), [filters, model.modules]);
  const visibleModuleGroups = useMemo(() => promptModuleDisplayGroups(visibleModules, copy), [copy, visibleModules]);

  const applyCreate = async () => {
    if (!createPreview.available) {
      setCreateMessage(createPreview.contractGap || copy.actions.unavailable);
      return;
    }
    if (createConfirmedKey !== createPreview.confirmationKey) {
      setCreateMessage(copy.actions.confirmationRequired);
      return;
    }
    setCreateMessage(copy.actions.applying);
    try {
      const created = await api.createPromptModule(createPreview.body);
      const id = createdPromptModuleId(created);
      if (!id) throw new Error('created prompt module id unavailable');
      if (createPreview.followUp) {
        await api.savePromptModuleDraft(id, createPreview.followUp.body);
      }
      setCreateMessage(copy.actions.applied);
      setCreateConfirmedKey('');
      setCreateForm({ name: '', displayName: '', description: '', markdown: '# New prompt\n', priority: 10, conditions: emptyPromptConditions });
      await load();
      setDrawer(null);
      if (typeof window !== 'undefined') window.location.hash = `/prompts/${encodeURIComponent(id)}`;
    } catch (error) {
      setCreateMessage(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <div className="page-stack">
      <ResourceHeader
        eyebrow={copy.header.meta.kindValue}
        title={model.header.title}
        description={model.header.description}
        meta={model.header.meta}
        actions={
          <div className="action-row">
            <button className="btn btn-primary" type="button" onClick={() => setDrawer('create')}>{copy.list.createPrompt}</button>
            <RefreshActionButton label={copy.list.refresh} busyLabel={copy.list.refreshing} loading={state.loading} onClick={load} />
          </div>
        }
      />
      <StatusStrip items={model.status} />
      <UnavailableNotice errors={state.errors} loading={state.loading} copy={copy} />
      {createMessage ? <div className="notice"><strong>{copy.actions.result}</strong> {createMessage}</div> : null}

      <section className="panel">
        <div className="panel-header prompt-module-panel-header">
          <div>
            <h2>{copy.list.modulesTitle}</h2>
            <p>{copy.list.modulesDescription}</p>
          </div>
        </div>
        <PromptModuleFilters filters={filters} copy={copy} onChange={next => setFilters(previous => ({ ...previous, ...next }))} />
        <p className="muted-inline">{copy.list.filterSummary(visibleModules.length, model.modules.length)}</p>
        <div className="prompt-module-groups">
          {visibleModuleGroups.map(group => <PromptModuleGroupSection key={group.id} group={group} copy={copy} />)}
        </div>
      </section>

      <Drawer title={copy.lifecycle.createManaged} open={drawer === 'create'} onClose={() => setDrawer(null)} badge="guarded">
        <CreateManagedPromptPanel
          form={createForm}
          preview={createPreview}
          confirmed={createConfirmedKey === createPreview.confirmationKey}
          copy={copy}
          onChange={next => {
            setCreateForm(previous => ({ ...previous, ...next }));
            setCreateConfirmedKey('');
          }}
          onConfirm={checked => setCreateConfirmedKey(checked ? createPreview.confirmationKey : '')}
          onCreate={() => void applyCreate()}
        />
      </Drawer>
    </div>
  );
}

export function groupPromptModules(modules: PromptModuleRow[]): PromptModuleGroup[] {
  const groups: Record<PromptModuleGroupId, PromptModuleRow[]> = { core: [], builtin: [], console: [] };
  modules.forEach(module => {
    if (module.classification === 'core') {
      groups.core.push(module);
      return;
    }
    if (module.classification === 'managed') {
      groups.console.push(module);
      return;
    }
    groups.builtin.push(module);
  });
  return [
    { id: 'core', modules: groups.core },
    { id: 'builtin', modules: groups.builtin },
    { id: 'console', modules: groups.console },
  ];
}

function promptModuleDisplayGroups(modules: PromptModuleRow[], copy: PromptControlPlaneCopy): PromptModuleDisplayGroup[] {
  const descriptions: Record<PromptModuleGroupId, { title: string; description: string }> = {
    core: { title: copy.list.groups.coreTitle, description: copy.list.groups.coreDescription },
    builtin: { title: copy.list.groups.builtinTitle, description: copy.list.groups.builtinDescription },
    console: { title: copy.list.groups.consoleTitle, description: copy.list.groups.consoleDescription },
  };
  return groupPromptModules(modules).map(group => ({ ...group, ...descriptions[group.id] }));
}

export function PromptModuleDetailPage({ moduleId, initialTab, initialAction }: { moduleId: string; initialTab?: string | null; initialAction?: string | null }) {
  const { t } = useI18n();
  const copy = useMemo(() => createPromptControlPlaneCopy(t), [t]);
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<DetailTab>(() => normalizeDetailTab(initialTab));
  const [drawer, setDrawer] = useState<DetailDrawer>(() => normalizeDetailDrawer(initialAction));
  const [confirmation, setConfirmation] = useState<DetailConfirmation>(emptyDetailConfirmation);
  const [lifecycleConfirmation, setLifecycleConfirmation] = useState<LifecycleConfirmation>(emptyLifecycleConfirmation);
  const [lifecyclePreview, setLifecyclePreview] = useState<PromptMutationPreview | null>(null);
  const [editorForm, setEditorForm] = useState<PromptEditorFormState>(emptyPromptEditorForm);
  const [editorTouchedMarkdown, setEditorTouchedMarkdown] = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);
  const [selectedTargets, setSelectedTargets] = useState<Record<string, string[]>>({});
  const [state, setState] = useState<DetailState>(() => {
    const sources = { moduleId };
    return { loading: true, sources, model: buildPromptModuleDetailModel(sources, copy), errors: {}, actionMessage: '' };
  });
  const tabs = useMemo<Array<{ id: DetailTab; label: string }>>(() => [
    { id: 'overview', label: copy.detail.tabs.overview },
    { id: 'evidence', label: copy.detail.tabs.evidence },
    { id: 'history', label: copy.detail.tabs.history },
  ], [copy]);

  const load = useCallback(async () => {
    const errors: Record<string, string> = {};
    setConfirmation(emptyDetailConfirmation);
    setState(previous => ({ ...previous, loading: true }));
    const [moduleList, detail, preview, versions, consumers] = await Promise.all([
      capture(copy.list.modulesTitle, api.getPromptModules(), errors),
      capture(copy.detail.moduleId, api.getPromptModule(moduleId), errors),
      capture(copy.status.preview, api.getPromptModulePreview(moduleId), errors),
      capture(copy.detail.versions, api.getPromptModuleVersions(moduleId), errors),
      capture(copy.status.consumers, api.getPromptModuleConsumers(moduleId), errors),
    ]);
    const sources = { moduleId, moduleList, detail, preview, versions, consumers, errors };
    const nextModel = buildPromptModuleDetailModel(sources, copy);
    setEditorForm(promptEditorFormFromModel(nextModel));
    setEditorTouchedMarkdown(false);
    setEditorDirty(false);
    setLifecyclePreview(null);
    setLifecycleConfirmation(emptyLifecycleConfirmation);
    setState(previous => ({
      loading: false,
      sources,
      model: nextModel,
      errors,
      actionMessage: previous.actionMessage,
    }));
  }, [api, copy, moduleId]);

  useEffect(() => {
    setActiveTab(normalizeDetailTab(initialTab));
  }, [initialTab, moduleId]);

  useEffect(() => {
    setDrawer(normalizeDetailDrawer(initialAction));
  }, [initialAction, moduleId]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedKeysFor = (action: PromptModuleActionKind, candidates: PromptEvidenceCandidate[]) => selectedTargets[action] ?? candidates.map(candidate => candidate.key);

  const toggleTarget = (action: PromptModuleActionKind, candidate: PromptEvidenceCandidate, checked: boolean, candidates: PromptEvidenceCandidate[]) => {
    setConfirmation(emptyDetailConfirmation);
    setSelectedTargets(previous => {
      const current = previous[action] ?? candidates.map(item => item.key);
      const next = checked
        ? Array.from(new Set([...current, candidate.key]))
        : current.filter(key => key !== candidate.key);
      return { ...previous, [action]: next };
    });
  };

  const runAction = async (action: Exclude<DetailAction, null>) => {
    const { model } = state;
    const candidates = actionCandidates(model, action);
    const control = buildPromptModuleActionControl(model, action, copy, selectedKeysFor(action, candidates));
    if (!control.available) {
      setState(previous => ({ ...previous, actionMessage: control.contractGap || copy.actions.unavailable }));
      return;
    }
    if (confirmation.action !== action || confirmation.confirmationKey !== control.confirmationKey) {
      setState(previous => ({ ...previous, actionMessage: copy.actions.confirmationRequired }));
      return;
    }
    setState(previous => ({ ...previous, actionMessage: copy.actions.applying }));
    try {
      if (action === 'create_override') {
        const result = await api.createBuiltinPromptModuleOverride(model.name, control.body);
        const overrideId = createdOverrideId(result);
        setConfirmation(emptyDetailConfirmation);
        setState(previous => ({ ...previous, actionMessage: overrideId ? copy.actions.overrideCreatedOpen : copy.actions.overrideCreatedRefresh }));
        if (overrideId && typeof window !== 'undefined') {
          window.location.hash = `/prompts/${encodeURIComponent(overrideId)}`;
          return;
        }
      } else if (action === 'create_version') {
        await api.createPromptModuleVersion(moduleId, control.body);
      } else if (action === 'publish') {
        const version = typeof control.body.version === 'number' ? control.body.version : model.evidence.publishOnly.version;
        await api.publishPromptModule(moduleId, version);
      } else if (action === 'rollout_all') {
        await api.rolloutPromptModule(moduleId, control.body);
      } else if (action === 'rollout_selected' || action === 'rollback' || action === 'rollback_builtin_default') {
        await api.rolloutPromptModule(moduleId, control.body);
      }
      setConfirmation(emptyDetailConfirmation);
      setState(previous => ({ ...previous, actionMessage: copy.actions.applied }));
      await load();
    } catch (error) {
      setState(previous => ({ ...previous, actionMessage: error instanceof Error ? error.message : String(error) }));
    }
  };

  const updateEditorForm = (next: Partial<PromptEditorFormState>, options: { markdownTouched?: boolean } = {}) => {
    setEditorForm(previous => ({ ...previous, ...next }));
    setEditorDirty(true);
    if (options.markdownTouched) setEditorTouchedMarkdown(true);
    setLifecyclePreview(null);
    setLifecycleConfirmation(emptyLifecycleConfirmation);
  };

  const discardEditorDraft = () => {
    setEditorForm(promptEditorFormFromModel(state.model));
    setEditorDirty(false);
    setEditorTouchedMarkdown(false);
    setLifecyclePreview(null);
    setLifecycleConfirmation(emptyLifecycleConfirmation);
  };

  const openLifecyclePreview = (kind: PromptArtifactLifecycleKind) => {
    const preview = buildPromptArtifactLifecyclePreview(state.model, kind, copy, promptDraftInputFromEditorForm(editorForm, editorTouchedMarkdown));
    setLifecyclePreview(preview);
    setLifecycleConfirmation(emptyLifecycleConfirmation);
    setDrawer(kind);
  };

  const applyLifecyclePreview = async () => {
    if (!lifecyclePreview?.available) return;
    if (lifecycleConfirmation.kind !== lifecyclePreview.kind || lifecycleConfirmation.confirmationKey !== lifecyclePreview.confirmationKey) {
      setState(previous => ({ ...previous, actionMessage: copy.actions.confirmationRequired }));
      return;
    }
    setState(previous => ({ ...previous, actionMessage: copy.actions.applying }));
    try {
      if (lifecyclePreview.kind === 'save_draft') {
        await api.savePromptModuleDraft(moduleId, lifecyclePreview.body);
      } else if (lifecyclePreview.kind === 'archive') {
        await api.updatePromptModuleStatus(moduleId, lifecyclePreview.body);
      } else if (lifecyclePreview.kind === 'delete') {
        await api.deletePromptModule(moduleId);
      }
      setLifecyclePreview(null);
      setLifecycleConfirmation(emptyLifecycleConfirmation);
      setState(previous => ({ ...previous, actionMessage: copy.actions.applied }));
      await load();
      setDrawer(null);
      if (lifecyclePreview.kind === 'delete' && typeof window !== 'undefined') window.location.hash = '/prompts';
    } catch (error) {
      setState(previous => ({ ...previous, actionMessage: error instanceof Error ? error.message : String(error) }));
    }
  };

  const { model } = state;
  const detailControls: PromptModuleActionKind[] = ['create_override', 'create_version', 'publish'];
  const activeAction = isPromptModuleActionKind(drawer) ? drawer : null;
  const actionCandidatesForDrawer = activeAction ? actionCandidates(model, activeAction) : [];
  const actionControl = activeAction ? buildPromptModuleActionControl(model, activeAction, copy, selectedKeysFor(activeAction, actionCandidatesForDrawer)) : null;
  const activeLifecyclePreview = drawer === 'save_draft'
    ? buildPromptArtifactLifecyclePreview(model, 'save_draft', copy, promptDraftInputFromEditorForm(editorForm, editorTouchedMarkdown))
    : (drawer === 'archive' || drawer === 'delete') ? lifecyclePreview : null;
  const draftJsonModel = buildPromptDraftJsonDrawerModel(model, { markdown: editorForm.markdown, priority: editorForm.priority, conditions: editorForm.conditions });
  const dirtyGuard = useDirtyDraftGuard({ dirty: editorDirty, message: defaultDirtyDraftGuardMessage });
  const guardedSetDrawer = (nextDrawer: DetailDrawer) => {
    if (nextDrawer === null && drawer === 'edit' && editorDirty) {
      dirtyGuard.guardDrawerClose(() => setDrawer(null));
      return;
    }
    setDrawer(nextDrawer);
  };
  const selectDetailTab = (tab: string) => {
    const next = normalizeDetailTab(tab);
    if (next === activeTab) return;
    dirtyGuard.guardTabSwitch(next, selectedTab => {
      setActiveTab(normalizeDetailTab(selectedTab));
      if (typeof window !== 'undefined') {
        window.history.replaceState(null, '', `#/prompts/${encodeURIComponent(moduleId)}?tab=${next}`);
      }
    });
  };

  return (
    <div className="page-stack">
      <ResourceHeader
        eyebrow={copy.detail.eyebrow}
        title={model.displayName}
        description={copy.detail.description}
        meta={model.meta}
        actions={
          <div className="action-row">
            <button className="btn btn-primary" type="button" onClick={() => guardedSetDrawer('edit')}>{model.editor.editable ? copy.detail.openEditorDrawer : copy.detail.viewMarkdownDrawer}</button>
            <button className="btn btn-subtle" type="button" onClick={() => guardedSetDrawer('config_json')}>{copy.detail.configJsonAction}</button>
            <a className="btn btn-subtle" href="#/prompts">{copy.detail.allPrompts}</a>
            <RefreshActionButton label={copy.detail.refreshEvidence} busyLabel={copy.list.refreshing} loading={state.loading} onClick={load} />
          </div>
        }
      />
      <StatusStrip items={model.status} />
      <UnavailableNotice errors={state.errors} loading={state.loading} copy={copy} />
      {state.actionMessage ? <div className="notice"><strong>{copy.actions.result}</strong> {state.actionMessage}</div> : null}

      <Tabs tabs={tabs} activeId={activeTab} onSelect={selectDetailTab} />

      {activeTab === 'overview' ? (
        <>
        <section className="content-grid">
          <div className="panel">
            <div className="panel-header">
              <div>
                <h2>{copy.detail.classificationTitle}</h2>
                <p>{copy.detail.classificationDescription}</p>
              </div>
            </div>
            <PropertyTable rows={[
              [copy.status.classification, model.classification],
              [copy.detail.name, model.name],
              [copy.detail.versions, String(model.versions.length)],
              [copy.status.consumers, String(model.consumers.length)],
            ]} />
          </div>
          <div className="panel">
            <div className="panel-header">
              <div>
                <h2>{copy.detail.redactedPreviewTitle}</h2>
                <p>{copy.detail.redactedPreviewDescription}</p>
              </div>
            </div>
            <PropertyTable rows={model.previewSummary} />
          </div>
        </section>
        <PromptContentPanelsView model={model} copy={copy} />
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>{copy.detail.editorTitle}</h2>
              <p>{model.editor.editable ? copy.detail.editorDescription : model.editor.contractGap}</p>
            </div>
            <div className="action-row">
              <button className="btn btn-subtle" type="button" onClick={() => guardedSetDrawer('edit')}>{model.editor.editable ? copy.detail.openEditorDrawer : copy.detail.viewMarkdownDrawer}</button>
            </div>
          </div>
          <MarkdownPreview markdown={redactSensitiveText(editorForm.markdown || model.content.primaryMarkdown)} />
        </section>
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>{copy.detail.usageTitle}</h2>
              <p>{copy.detail.usageDescription}</p>
            </div>
          </div>
          <PromptUsageEvidenceTable consumers={model.consumers} copy={copy} />
        </section>
        </>
      ) : null}

      {activeTab === 'evidence' ? (
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>{copy.detail.actionEvidenceTitle}</h2>
              <p>{copy.detail.actionEvidenceDescription}</p>
            </div>
          </div>
          <div className="content-grid">
            {detailControls.map(action => {
              const candidates = actionCandidates(model, action);
              const selectedKeys = selectedKeysFor(action, candidates);
              const control = buildPromptModuleActionControl(model, action, copy, selectedKeys);
              return (
                <ActionSummaryCard
                  key={action}
                  control={control}
                  candidateOptions={candidates}
                  copy={copy}
                  onOpen={() => guardedSetDrawer(action)}
                />
              );
            })}
            <PromptArtifactLifecycleActionCard
              preview={buildPromptArtifactLifecyclePreview(model, 'archive', copy)}
              actionLabel={copy.detail.archivePreview}
              copy={copy}
              onPreview={() => openLifecyclePreview('archive')}
            />
            <PromptArtifactLifecycleActionCard
              preview={buildPromptArtifactLifecyclePreview(model, 'delete', copy)}
              actionLabel={copy.detail.deletePreview}
              copy={copy}
              onPreview={() => openLifecyclePreview('delete')}
            />
          </div>
          <PromptUsageEvidenceTable consumers={model.consumers} copy={copy} />
        </section>
      ) : null}

      {activeTab === 'history' ? (
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>{copy.detail.versionHistoryTitle}</h2>
              <p>{copy.detail.versionHistoryDescription}</p>
            </div>
          </div>
          <PromptVersionHistoryTable versions={model.versions} copy={copy} />
        </section>
      ) : null}

      <Drawer title={copy.detail.editorTitle} open={drawer === 'edit'} onClose={() => guardedSetDrawer(null)} badge={model.editor.editable ? 'editable' : 'readonly'}>
        <PromptMarkdownEditorPanel
          model={model}
          copy={copy}
          form={editorForm}
          dirty={editorDirty}
          onChange={updateEditorForm}
          onPreviewSave={() => openLifecyclePreview('save_draft')}
          onDiscard={discardEditorDraft}
          onOpenDraftJson={() => guardedSetDrawer('draft_json')}
        />
      </Drawer>

      <Drawer title={copy.detail.draftJsonTitle} open={drawer === 'draft_json'} onClose={() => guardedSetDrawer(null)} badge="redacted">
        <PromptDraftJsonDrawerContent draftJsonDrawer={draftJsonModel} copy={copy} />
      </Drawer>

      <Drawer title={copy.detail.configJsonAction} open={drawer === 'config_json'} onClose={() => guardedSetDrawer(null)} badge="redacted">
        <p>{copy.detail.rawDescription}</p>
        <pre className="json-preview">{model.rawJson}</pre>
      </Drawer>

      <Drawer title={activeAction ? actionControl?.title ?? copy.detail.actionEvidenceTitle : copy.detail.actionEvidenceTitle} open={Boolean(activeAction)} onClose={() => guardedSetDrawer(null)} badge={actionControl?.available ? 'guarded' : 'unavailable'}>
        {activeAction && actionControl ? (
          <ActionCard
            control={actionControl}
            candidateOptions={actionCandidatesForDrawer}
            confirm={confirmation.action === activeAction && confirmation.confirmationKey === actionControl.confirmationKey}
            selectedKeys={selectedKeysFor(activeAction, actionCandidatesForDrawer)}
            copy={copy}
            onConfirm={checked => setConfirmation(checked ? { action: activeAction, confirmationKey: actionControl.confirmationKey } : emptyDetailConfirmation)}
            onRun={() => activeAction !== 'destructive' ? void runAction(activeAction) : undefined}
            onToggleCandidate={(candidate, checked) => toggleTarget(activeAction, candidate, checked, actionCandidatesForDrawer)}
          />
        ) : null}
      </Drawer>

      <Drawer title={activeLifecyclePreview?.title ?? copy.detail.editorTitle} open={drawer === 'save_draft' || drawer === 'archive' || drawer === 'delete'} onClose={() => guardedSetDrawer(null)} badge={activeLifecyclePreview?.available ? 'guarded' : 'unavailable'}>
        <ArtifactLifecyclePreviewPanel
          preview={activeLifecyclePreview}
          confirmed={Boolean(activeLifecyclePreview?.available && lifecycleConfirmation.confirmationKey === activeLifecyclePreview.confirmationKey)}
          copy={copy}
          onConfirm={checked => setLifecycleConfirmation(checked && activeLifecyclePreview ? { kind: activeLifecyclePreview.kind as PromptArtifactLifecycleKind, confirmationKey: activeLifecyclePreview.confirmationKey } : emptyLifecycleConfirmation)}
          onRun={() => void applyLifecyclePreview()}
        />
      </Drawer>
    </div>
  );
}

export function PromptAssignmentsPage({ personaId, platform }: { personaId: string; platform?: string | null }) {
  const { t } = useI18n();
  const copy = useMemo(() => createPromptControlPlaneCopy(t), [t]);
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<AssignmentTab>('overview');
  const [preview, setPreview] = useState<PromptMutationPreview | null>(null);
  const [confirmedPreviewKey, setConfirmedPreviewKey] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const selectedPlatform = normalizePlatform(platform);
  const [state, setState] = useState<AssignmentState>(() => {
    const sources = { personaId, platform: selectedPlatform };
    return { loading: true, sources, model: buildPromptAssignmentModel(sources, copy), errors: {} };
  });
  const tabs = useMemo<Array<{ id: AssignmentTab; label: string }>>(() => [
    { id: 'overview', label: copy.assignments.tabs.overview },
    { id: 'modules', label: copy.assignments.tabs.modules },
    { id: 'edit', label: copy.assignments.tabs.editPreview },
    { id: 'raw', label: copy.assignments.tabs.rawJson },
  ], [copy]);

  const load = useCallback(async () => {
    const errors: Record<string, string> = {};
    setPreview(null);
    setConfirmedPreviewKey('');
    setState(previous => ({ ...previous, loading: true }));
    const [persona, assignment] = await Promise.all([
      capture(copy.assignments.persona, api.getPersona(personaId), errors),
      capture(copy.status.assignment, api.getPromptModuleAssignments(personaId, selectedPlatform), errors),
    ]);
    const sources = { personaId, platform: selectedPlatform, persona, assignment, errors };
    setState({ loading: false, sources, model: buildPromptAssignmentModel(sources, copy), errors });
  }, [api, copy, personaId, selectedPlatform]);

  useEffect(() => {
    void load();
  }, [load]);

  const switchPlatform = (next: string) => {
    if (typeof window === 'undefined') return;
    const suffix = next ? `?platform=${encodeURIComponent(next)}` : '';
    window.location.hash = `/personas/${encodeURIComponent(personaId)}/prompts${suffix}`;
  };

  const openAssignmentPreview = (moduleName: string, nextEnabled: boolean) => {
    const nextPreview = buildPromptAssignmentMutationPreview(state.model, moduleName, nextEnabled, copy);
    setPreview(nextPreview);
    setConfirmedPreviewKey('');
    setActionMessage('');
    setActiveTab('edit');
  };

  const openResetPreview = () => {
    const nextPreview = buildResetPlatformAssignmentPreview(state.model, copy);
    setPreview(nextPreview);
    setConfirmedPreviewKey('');
    setActionMessage('');
    setActiveTab('edit');
  };

  const applyAssignmentPreview = async () => {
    if (!preview?.available) return;
    if (confirmedPreviewKey !== preview.confirmationKey) {
      setActionMessage(copy.actions.assignmentConfirmationRequired);
      return;
    }
    setActionMessage(copy.actions.applyingAssignment);
    try {
      await api.updatePromptModuleAssignments(personaId, preview.body);
      setPreview(null);
      setConfirmedPreviewKey('');
      setActionMessage(copy.actions.assignmentSaved);
      await load();
    } catch (error) {
      setActionMessage(error instanceof Error ? error.message : String(error));
    }
  };

  const { model } = state;

  return (
    <div className="page-stack">
      <ResourceHeader
        eyebrow={copy.assignments.eyebrow}
        title={`${model.personaName} ${copy.assignments.titleSuffix}`}
        description={copy.assignments.description}
        meta={[
          [copy.assignments.persona, model.personaId],
          [copy.assignments.scope, model.scopeLabel],
          [copy.assignments.source, model.sourceState],
          [copy.status.draft, model.draftRevision || copy.common.unavailable],
        ]}
        actions={
          <div className="action-row">
            <label className="field-label">
              {copy.assignments.platform}
              <select value={selectedPlatform} onChange={event => switchPlatform(event.target.value)}>
                <option value="">{copy.assignments.defaultPersona}</option>
                {model.platforms.map(item => <option key={item} value={item}>{item}</option>)}
                {!model.platforms.includes('linear') ? <option value="linear">linear</option> : null}
                {!model.platforms.includes('lark') ? <option value="lark">lark</option> : null}
              </select>
            </label>
            <a className="btn btn-subtle" href="#/prompts">{copy.detail.allPrompts}</a>
            <RefreshActionButton label={copy.assignments.refresh} busyLabel={copy.assignments.refreshing} loading={state.loading} onClick={load} />
          </div>
        }
      />
      <StatusStrip items={model.status} />
      <UnavailableNotice errors={state.errors} loading={state.loading} copy={copy} />
      {actionMessage ? <div className="notice"><strong>{copy.actions.result}</strong> {actionMessage}</div> : null}

      <Tabs tabs={tabs} activeId={activeTab} onSelect={tab => setActiveTab(normalizeAssignmentTab(tab))} />

      {activeTab === 'overview' ? (
        <section className="content-grid">
          <div className="panel">
            <div className="panel-header">
              <div>
                <h2>{copy.assignments.sourceTitle}</h2>
                <p>{copy.assignments.sourceDescription}</p>
              </div>
            </div>
            <PropertyTable rows={[
              [copy.assignments.scope, model.scopeLabel],
              [copy.assignments.sourceState, model.sourceState],
              [copy.list.table.evidence, model.sourceEvidence],
              [copy.assignments.draftChanged, model.draftChanged ? copy.common.yes : copy.common.no],
            ]} />
          </div>
          <div className="panel">
            <div className="panel-header">
              <div>
                <h2>{copy.assignments.mutationTitle}</h2>
                <p>{copy.assignments.mutationDescription}</p>
              </div>
            </div>
            <a className="btn btn-subtle" href="#" onClick={event => { event.preventDefault(); setActiveTab('edit'); }}>{copy.assignments.openEditPreview}</a>
          </div>
        </section>
      ) : null}

      {activeTab === 'modules' ? (
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>{copy.assignments.modulesTitle}</h2>
              <p>{copy.assignments.modulesDescription}</p>
            </div>
          </div>
          <AssignmentTable modules={model.modules} copy={copy} />
        </section>
      ) : null}

      {activeTab === 'edit' ? (
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>{copy.assignments.edit.title}</h2>
              <p>{copy.assignments.edit.description}</p>
            </div>
            {model.platform ? <button className="btn btn-subtle" type="button" onClick={openResetPreview}>{copy.assignments.edit.reset}</button> : null}
          </div>
          <div className="content-grid">
            <div className="record-list">
              {model.modules.map(module => (
                <div className="record-card" key={`${module.name}:${module.assignmentSource}:edit`}>
                  <strong>{module.displayName}</strong>
                  <p>{module.name} · {module.origin} · {module.enabled ? copy.common.enabled : copy.common.disabled} · {module.configurable ? copy.common.configurable : copy.common.locked}</p>
                  <button className="btn btn-compact" type="button" disabled={!module.configurable} onClick={() => openAssignmentPreview(module.name, !module.enabled)}>
                    {module.enabled ? copy.assignments.edit.previewDisable : copy.assignments.edit.previewEnable}
                  </button>
                </div>
              ))}
            </div>
            <MutationPreviewPanel
              preview={preview}
              confirmed={Boolean(preview?.available && confirmedPreviewKey === preview.confirmationKey)}
              copy={copy}
              onConfirm={checked => setConfirmedPreviewKey(checked && preview ? preview.confirmationKey : '')}
              onRun={() => void applyAssignmentPreview()}
            />
          </div>
        </section>
      ) : null}

      {activeTab === 'raw' ? (
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>{copy.list.rawJson}</h2>
              <p>{copy.detail.rawDescription}</p>
            </div>
          </div>
          <pre className="json-preview">{model.rawJson}</pre>
        </section>
      ) : null}
    </div>
  );
}

export function PromptContentPanelsView({ model, copy }: { model: PromptModuleDetailModel; copy: PromptControlPlaneCopy }) {
  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <h2>{copy.detail.contentTitle}</h2>
          <p>{copy.detail.contentDescription}</p>
        </div>
      </div>
      <div className="content-grid">
        {model.contentPanels.map(panel => (
          <div className="record-card" key={`${panel.title}:${panel.source}`}>
            <strong>{panel.title}</strong>
            <p>{panel.source}</p>
            <MarkdownPreview markdown={panel.markdown} />
          </div>
        ))}
      </div>
    </section>
  );
}

export function PromptMarkdownEditorPanel({
  model,
  copy,
  form,
  dirty = false,
  onChange,
  onPreviewSave,
  onDiscard,
  onOpenDraftJson,
}: {
  model: PromptModuleDetailModel;
  copy: PromptControlPlaneCopy;
  form: PromptEditorFormState;
  dirty?: boolean;
  onChange: (next: Partial<PromptEditorFormState>, options?: { markdownTouched?: boolean }) => void;
  onPreviewSave: () => void;
  onDiscard?: () => void;
  onOpenDraftJson?: () => void;
}) {
  const displayMarkdown = redactSensitiveText(form.markdown);
  const markdownDisplayRedacted = displayMarkdown !== form.markdown;
  const markdownEditable = model.editor.editable && !markdownDisplayRedacted;
  const agentConditionOptions = promptAgentConditionOptions(model);
  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <h2>{copy.detail.editorTitle}</h2>
          <p>{markdownDisplayRedacted ? copy.detail.redactedEditorLocked : model.editor.editable ? copy.detail.editorDescription : model.editor.contractGap}</p>
        </div>
      </div>
      <div className="form-grid">
        <label className="field-label">
          {copy.lifecycle.displayName}
          <input disabled={!model.editor.editable} value={form.displayName} onChange={event => onChange({ displayName: event.target.value })} />
        </label>
        <label className="field-label">
          {copy.detail.status}
          <select disabled value={form.status} onChange={event => onChange({ status: event.target.value })}>
            {['draft', 'active', 'disabled', 'archived', 'builtin'].map(status => <option key={status} value={status}>{status}</option>)}
          </select>
        </label>
        <label className="field-label form-span">
          {copy.lifecycle.description}
          <input disabled={!model.editor.editable} value={form.description} onChange={event => onChange({ description: event.target.value })} />
        </label>
        <label className="field-label">
          {copy.status.classification}
          <select disabled value={form.classification} onChange={event => onChange({ classification: event.target.value as PromptModuleRow['classification'] })}>
            <option value="core">core</option>
            <option value="builtin default">builtin default</option>
            <option value="managed override">managed override</option>
            <option value="managed">managed</option>
          </select>
        </label>
        <label className="field-label">
          {copy.assignments.scope}
          <select disabled value={form.scope} onChange={event => onChange({ scope: event.target.value })}>
            {['core', 'builtin', 'project', 'workspace', 'role', 'skill', 'platform'].map(scope => <option key={scope} value={scope}>{scope}</option>)}
          </select>
        </label>
        <label className="field-label form-span">
          {copy.detail.overrideTarget}
          <input disabled value={form.overrideTarget || copy.common.none} onChange={event => onChange({ overrideTarget: event.target.value })} />
        </label>
        <label className="field-label">
          {copy.detail.priority}
          <input type="number" disabled={!model.editor.editable} value={form.priority} onChange={event => onChange({ priority: Number(event.target.value) })} />
        </label>
        <div className="field-label form-span">
          {copy.detail.conditionsTitle}
          <div className="content-grid prompt-condition-grid">
            <PromptMultiSelect label={copy.detail.platformConditions} options={promptConditionPlatformOptions} values={form.conditions.platforms} disabled={!model.editor.editable} onChange={platforms => onChange({ conditions: { ...form.conditions, platforms } })} />
            <PromptMultiSelect label={copy.detail.agentConditions} options={agentConditionOptions} values={form.conditions.agents} disabled={!model.editor.editable} onChange={agents => onChange({ conditions: { ...form.conditions, agents } })} />
          </div>
        </div>
        <label className="field-label form-span">
          {copy.detail.markdown}
          <textarea rows={10} disabled={!markdownEditable} value={displayMarkdown} onChange={event => onChange({ markdown: event.target.value }, { markdownTouched: true })} />
        </label>
        <div className="content-grid form-span">
          <div>
            <strong>{copy.detail.markdownPreview}</strong>
            <MarkdownPreview markdown={displayMarkdown} />
          </div>
          <div className="record-card">
            <strong>{copy.detail.editorDraftState}</strong>
            <p>{dirty ? copy.detail.editorDirty : copy.detail.editorClean}</p>
            <PropertyTable rows={[
              [copy.detail.previewRevision, String(model.editor.baseRevision)],
              [copy.lifecycle.priority, String(form.priority)],
              [copy.detail.overrideTarget, form.overrideTarget || copy.common.none],
            ]} />
          </div>
        </div>
        <div className="action-row form-span prompt-editor-actions">
          <button className="btn" type="button" disabled={!markdownEditable} onClick={onPreviewSave}>{copy.detail.saveDraftPreview}</button>
          {onDiscard ? <button className="btn btn-subtle" type="button" disabled={!dirty} onClick={onDiscard}>{copy.detail.discardDraft}</button> : null}
          {onOpenDraftJson ? <button className="btn" type="button" onClick={onOpenDraftJson}>{copy.detail.draftJsonAction}</button> : null}
        </div>
      </div>
    </div>
  );
}

function PromptMultiSelect({ label, options, values, disabled, onChange }: { label: string; options: string[]; values: string[]; disabled?: boolean; onChange: (values: string[]) => void }) {
  const toggle = (option: string, checked: boolean) => {
    const next = checked ? Array.from(new Set([...values, option])) : values.filter(value => value !== option);
    onChange(next);
  };
  return (
    <div className="record-card prompt-condition-card">
      <strong>{label}</strong>
      {options.length ? (
        <div className="config-segmented prompt-segmented" role="group" aria-label={label}>
          {options.map(option => (
            <button key={option} className={values.includes(option) ? 'active' : ''} type="button" disabled={disabled} onClick={() => toggle(option, !values.includes(option))}>{option}</button>
          ))}
        </div>
      ) : null}
      <input disabled={disabled} value={values.join(', ')} onChange={event => onChange(csvToList(event.target.value))} placeholder="comma,separated" />
    </div>
  );
}

export function PromptUsageEvidenceTable({ consumers, copy }: { consumers: PromptModuleDetailModel['consumers']; copy: PromptControlPlaneCopy }) {
  if (!consumers.length) {
    return <div className="empty-state"><strong>{copy.detail.noConsumersTitle}</strong><p>{copy.detail.noConsumersDescription}</p></div>;
  }
  return <ConsumerTable consumers={consumers} copy={copy} linkPersonaConfig />;
}

function PromptVersionHistoryTable({ versions, copy }: { versions: PromptModuleDetailModel['versions']; copy: PromptControlPlaneCopy }) {
  if (!versions.length) {
    return <div className="empty-state"><strong>{copy.status.unpublished}</strong><p>{copy.modelEvidence.noVersion}</p></div>;
  }
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>{copy.detail.versions}</th>
          <th>{copy.status.preview}</th>
          <th>{copy.detail.previewValidation}</th>
          <th>{copy.detail.status}</th>
        </tr>
      </thead>
      <tbody>
        {versions.map(version => {
          const validation = version.validation_result && typeof version.validation_result === 'object' && !Array.isArray(version.validation_result)
            ? version.validation_result as Record<string, unknown>
            : {};
          return (
            <tr key={String(version.version)}>
              <td><strong>v{String(version.version)}</strong><br /><small>{String(version.created_at ?? '')}</small></td>
              <td>{String(version.changelog ?? copy.common.none)}</td>
              <td>{validation.valid === true ? copy.modelEvidence.valid : copy.common.unavailable}</td>
              <td>{String(version.created_by ?? copy.common.unavailable)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function PromptDraftJsonDrawerContent({ draftJsonDrawer, copy }: { draftJsonDrawer: PromptModuleDetailModel['draftJsonDrawer']; copy: PromptControlPlaneCopy }) {
  return (
    <div className="record-list">
      <p>{copy.detail.draftJsonDescription}</p>
      <div>
        <h3>{copy.detail.draftJsonTitle}</h3>
        <pre className="json-preview">{draftJsonDrawer.draftJson}</pre>
      </div>
      <div>
        <h3>{copy.status.published}</h3>
        <pre className="json-preview">{draftJsonDrawer.publishedJson}</pre>
      </div>
      <div>
        <h3>Diff</h3>
        <pre className="json-preview">{draftJsonDrawer.diffText}</pre>
      </div>
    </div>
  );
}

function MarkdownPreview({ markdown }: { markdown: string }) {
  const nodes = markdownToNodes(markdown);
  return <div className="markdown-preview">{nodes}</div>;
}

function markdownToNodes(markdown: string) {
  const lines = markdown.split('\n');
  const nodes: ReactNode[] = [];
  let listItems: string[] = [];
  const flushList = () => {
    if (!listItems.length) return;
    const items = listItems;
    listItems = [];
    nodes.push(<ul key={`ul-${nodes.length}`}>{items.map(item => <li key={item}>{renderInlineMarkdown(item)}</li>)}</ul>);
  };
  lines.forEach((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      return;
    }
    const listMatch = trimmed.match(/^[-*]\s+(.+)$/);
    if (listMatch) {
      listItems.push(listMatch[1]);
      return;
    }
    flushList();
    if (trimmed.startsWith('### ')) nodes.push(<h3 key={index}>{renderInlineMarkdown(trimmed.slice(4))}</h3>);
    else if (trimmed.startsWith('## ')) nodes.push(<h2 key={index}>{renderInlineMarkdown(trimmed.slice(3))}</h2>);
    else if (trimmed.startsWith('# ')) nodes.push(<h1 key={index}>{renderInlineMarkdown(trimmed.slice(2))}</h1>);
    else nodes.push(<p key={index}>{renderInlineMarkdown(trimmed)}</p>);
  });
  flushList();
  return nodes;
}

function renderInlineMarkdown(value: string) {
  if (!value.includes('`')) return value;
  const parts = value.split(/(`[^`]+`)/g);
  return parts.map((part, index) => part.startsWith('`') && part.endsWith('`')
    ? <code key={index}>{part.slice(1, -1)}</code>
    : part);
}

function CreateManagedPromptPanel({
  form,
  preview,
  confirmed,
  copy,
  onChange,
  onConfirm,
  onCreate,
}: {
  form: CreateFormState;
  preview: PromptMutationPreview;
  confirmed: boolean;
  copy: PromptControlPlaneCopy;
  onChange: (next: Partial<CreateFormState>) => void;
  onConfirm: (checked: boolean) => void;
  onCreate: () => void;
}) {
  return (
    <div className="content-grid">
      <div>
        <div className="panel-header">
          <div>
            <h2>{copy.lifecycle.createManaged}</h2>
            <p>{copy.lifecycle.createDescription}</p>
          </div>
        </div>
        <div className="form-grid">
          <label className="field-label">
            {copy.lifecycle.name}
            <input value={form.name} onChange={event => onChange({ name: event.target.value })} placeholder="reviewer-guidance" />
          </label>
          <label className="field-label">
            {copy.lifecycle.displayName}
            <input value={form.displayName} onChange={event => onChange({ displayName: event.target.value })} placeholder="Reviewer guidance" />
          </label>
          <label className="field-label form-span">
            {copy.lifecycle.description}
            <input value={form.description} onChange={event => onChange({ description: event.target.value })} placeholder="Markdown 优先 Prompt 工件" />
          </label>
          <label className="field-label">
            {copy.lifecycle.priority}
            <input type="number" value={form.priority} onChange={event => onChange({ priority: Number(event.target.value) })} />
          </label>
          <label className="field-label form-span">
            {copy.lifecycle.markdown}
            <textarea rows={8} value={form.markdown} onChange={event => onChange({ markdown: event.target.value })} />
          </label>
          <div className="field-label form-span">
            {copy.detail.conditionsTitle}
            <div className="content-grid prompt-condition-grid">
              <PromptMultiSelect label={copy.detail.platformConditions} options={promptConditionPlatformOptions} values={form.conditions.platforms} onChange={platforms => onChange({ conditions: { ...form.conditions, platforms } })} />
              <PromptMultiSelect label={copy.detail.agentConditions} options={[]} values={form.conditions.agents} onChange={agents => onChange({ conditions: { ...form.conditions, agents } })} />
            </div>
          </div>
          <div className="form-span">
            <strong>{copy.detail.markdownPreview}</strong>
            <MarkdownPreview markdown={redactSensitiveText(form.markdown)} />
          </div>
        </div>
      </div>
      <div className="record-card">
        <strong>{copy.lifecycle.previewCreate}</strong>
        <p>{preview.evidence}</p>
        <RequestPreview preview={preview} copy={copy} />
        {preview.followUp ? (
          <div className="mutation-preview">
            <dl className="record-meta">
              <div><dt>{copy.previews.method}</dt><dd>{preview.followUp.method}</dd></div>
              <div><dt>{copy.previews.endpoint}</dt><dd>{preview.followUp.endpointTemplate}</dd></div>
            </dl>
            <pre>{preview.followUp.bodyJson}</pre>
          </div>
        ) : null}
        {preview.available ? (
          <>
            <label className="confirm-row">
              <input type="checkbox" checked={confirmed} onChange={event => onConfirm(event.target.checked)} />
              {copy.confirmations.reviewExactRequest}
            </label>
            <button className="btn btn-primary" type="button" disabled={!confirmed} onClick={onCreate}>{copy.lifecycle.createAndSeed}</button>
          </>
        ) : (
          <div className="notice notice-warning"><strong>{copy.previews.unavailableContractGap}</strong> {preview.contractGap}</div>
        )}
      </div>
    </div>
  );
}

function PromptModuleFilters({ filters, copy, onChange }: { filters: PromptListFilters; copy: PromptControlPlaneCopy; onChange: (next: Partial<PromptListFilters>) => void }) {
  return (
    <div className="action-row prompt-filter-row">
      <label className="field-label">
        {copy.list.filters.search}
        <input value={filters.search} onChange={event => onChange({ search: event.target.value })} placeholder={copy.list.filters.searchPlaceholder} />
      </label>
      <label className="field-label">
        {copy.list.filters.classification}
        <select value={filters.classification} onChange={event => onChange({ classification: event.target.value as PromptListFilters['classification'] })}>
          <option value="all">{copy.list.filters.allTypes}</option>
          <option value="core">core</option>
          <option value="builtin default">builtin default</option>
          <option value="managed override">managed override</option>
          <option value="managed">managed</option>
        </select>
      </label>
      <label className="field-label">
        {copy.list.filters.state}
        <select value={filters.state} onChange={event => onChange({ state: event.target.value as PromptListFilters['state'] })}>
          <option value="all">{copy.list.filters.allStates}</option>
          <option value="active">{copy.list.filters.active}</option>
          <option value="inactive">{copy.list.filters.inactive}</option>
          <option value="mutable">{copy.list.filters.mutable}</option>
          <option value="readonly">{copy.list.filters.readonly}</option>
          <option value="override_allowed">{copy.list.filters.overrideAllowed}</option>
        </select>
      </label>
    </div>
  );
}

function PromptModuleGroupSection({ group, copy }: { group: PromptModuleDisplayGroup; copy: PromptControlPlaneCopy }) {
  const sharedMetadata = promptModuleSharedMetadata(group.modules, copy);
  return (
    <section className="prompt-module-group" aria-labelledby={`prompt-module-group-${group.id}`}>
      <div className="prompt-module-group-header">
        <div>
          <h3 id={`prompt-module-group-${group.id}`}>{group.title}</h3>
          <p>{group.description}</p>
          {sharedMetadata.length ? (
            <dl className="prompt-module-group-summary" aria-label={copy.list.sharedMetadata}>
              {sharedMetadata.map(item => (
                <div key={item.label}>
                  <dt>{item.label}</dt>
                  <dd>{item.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
        <span className="pill">{copy.list.groupCount(group.modules.length)}</span>
      </div>
      {group.modules.length ? (
        <PromptModuleTable modules={group.modules} copy={copy} />
      ) : (
        <div className="empty-state prompt-module-group-empty">
          <strong>{copy.list.groups.emptyTitle}</strong>
          <p>{copy.list.groups.emptyDescription}</p>
        </div>
      )}
    </section>
  );
}

export function PromptModuleTable({ modules, copy }: { modules: PromptModuleRow[]; copy: PromptControlPlaneCopy }) {
  return (
    <table className="data-table prompt-module-table">
      <thead>
        <tr>
          <th>{copy.list.table.module}</th>
          <th>{copy.list.table.classification}</th>
          <th>{copy.list.table.source}</th>
          <th>{copy.list.table.details}</th>
          <th>{copy.list.table.actions}</th>
        </tr>
      </thead>
      <tbody>
        {modules.map(module => (
          <tr key={`${module.classification}:${module.id}:${module.name}`}>
            <td data-label={copy.list.table.module}><strong>{module.displayName}</strong><br /><small>{module.name}</small></td>
            <td data-label={copy.list.table.classification}><span className="badge">{module.classification}</span></td>
            <td data-label={copy.list.table.source}><PromptModuleSourceSummary module={module} copy={copy} /></td>
            <td data-label={copy.list.table.details} className="prompt-module-meta-cell"><PromptModuleInfoDisclosure module={module} copy={copy} /></td>
            <td data-label={copy.list.table.actions}>
              <div className="action-row">
                <a className="btn btn-compact btn-subtle" href={module.detailHref}>{copy.list.actions.open}</a>
                {module.mutable ? <a className="btn btn-compact btn-subtle" href={`${module.detailHref}?action=edit`}>{copy.list.actions.edit}</a> : null}
                {module.overrideAllowed && !module.mutable ? <a className="btn btn-compact btn-subtle" href={`${module.detailHref}?action=create_override`}>{copy.list.actions.override}</a> : null}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PromptModuleSourceSummary({ module, copy }: { module: PromptModuleRow; copy: PromptControlPlaneCopy }) {
  const warnings = promptModuleWarningLabels(module, copy);
  return (
    <>
      {module.contentSource}<br />
      <small>{module.status}</small>
      {warnings.length ? (
        <div className="prompt-module-warning-list">
          {warnings.map(warning => <span key={warning} className="pill prompt-module-warning-pill">{warning}</span>)}
        </div>
      ) : null}
    </>
  );
}

function PromptModuleInfoDisclosure({ module, copy }: { module: PromptModuleRow; copy: PromptControlPlaneCopy }) {
  return (
    <details className="prompt-module-info">
      <summary className="prompt-module-info-toggle" aria-label={copy.list.viewMetadata}>
        <span aria-hidden="true">i</span>
      </summary>
      <dl className="prompt-module-info-panel">
        <div>
          <dt>{copy.list.table.policy}</dt>
          <dd>{promptModulePolicyText(module, copy)}</dd>
        </div>
        <div>
          <dt>{copy.list.table.evidence}</dt>
          <dd>{module.evidence || copy.common.unavailable}</dd>
        </div>
      </dl>
    </details>
  );
}

export function promptModulePolicyText(module: PromptModuleRow, copy: PromptControlPlaneCopy) {
  return `${module.mutable ? copy.list.mutable : copy.list.immutable} · ${module.overrideAllowed ? copy.list.overrideAllowed : copy.list.overrideLocked}`;
}

export function promptModuleSharedMetadata(modules: PromptModuleRow[], copy: PromptControlPlaneCopy): PromptModuleSharedMetadataItem[] {
  const policy = uniquePromptModuleValue(modules, module => promptModulePolicyText(module, copy));
  const evidence = uniquePromptModuleValue(modules, module => module.evidence);
  return [
    policy ? { label: copy.list.table.policy, value: policy } : null,
    evidence ? { label: copy.list.table.evidence, value: evidence } : null,
  ].filter((item): item is PromptModuleSharedMetadataItem => Boolean(item));
}

function uniquePromptModuleValue(modules: PromptModuleRow[], valueForModule: (module: PromptModuleRow) => string) {
  const values = new Set(modules.map(module => valueForModule(module).trim()).filter(Boolean));
  return values.size === 1 ? [...values][0] : '';
}

function promptModuleWarningLabels(module: PromptModuleRow, copy: PromptControlPlaneCopy) {
  const warnings: string[] = [];
  const evidence = module.evidence.trim().toLowerCase();
  const unavailable = copy.common.unavailable.trim().toLowerCase();
  const status = module.status.trim().toLowerCase();
  if (!evidence || evidence === unavailable) warnings.push(copy.list.missingEvidence);
  if (status === 'unknown' || status.includes('draft') || status.includes('unpublished')) warnings.push(copy.list.draftStatus);
  if (module.classification !== 'core' && !module.mutable && !module.overrideAllowed) warnings.push(copy.list.policyBlocked);
  return Array.from(new Set(warnings));
}

function filterPromptModules(modules: PromptModuleRow[], filters: PromptListFilters) {
  const search = filters.search.trim().toLowerCase();
  return modules.filter(module => {
    if (filters.classification !== 'all' && module.classification !== filters.classification) return false;
    if (filters.state === 'active' && !module.enabled) return false;
    if (filters.state === 'inactive' && module.enabled) return false;
    if (filters.state === 'mutable' && !module.mutable) return false;
    if (filters.state === 'readonly' && module.mutable) return false;
    if (filters.state === 'override_allowed' && !module.overrideAllowed) return false;
    if (!search) return true;
    return [module.displayName, module.name, module.classification, module.contentSource, module.status, module.evidence].some(value => value.toLowerCase().includes(search));
  });
}

function ConsumerTable({ consumers, copy, linkPersonaConfig = false }: { consumers: PromptModuleDetailModel['consumers']; copy: PromptControlPlaneCopy; linkPersonaConfig?: boolean }) {
  if (!consumers.length) {
    return <div className="empty-state"><strong>{copy.detail.noConsumersTitle}</strong><p>{copy.detail.noConsumersDescription}</p></div>;
  }
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>{copy.detail.consumer}</th>
          <th>{copy.detail.platform}</th>
          <th>{copy.detail.referenced}</th>
          <th>{copy.detail.runtime}</th>
          <th>{copy.detail.actionEvidence}</th>
        </tr>
      </thead>
      <tbody>
        {consumers.map(consumer => (
          <tr key={consumer.key}>
            <td>
              <strong>{consumer.personaName}</strong><br />
              <small>{consumer.personaId}</small><br />
              {linkPersonaConfig ? <a href={consumer.configHref}>{copy.detail.openPersonaConfig}</a> : null}
            </td>
            <td>{consumer.platform || copy.common.defaultScope}</td>
            <td>{copy.detail.currentLatest(consumer.currentReferencedVersion || copy.common.builtin, consumer.latestPublishedVersion || copy.common.unavailable)}</td>
            <td>{consumer.runtimeState}<br /><small>{consumer.runtimeAppliedVersion ? copy.detail.runtimeVersion(consumer.runtimeAppliedVersion) : copy.common.unavailable}</small></td>
            <td>{consumer.requiredAction || consumer.status}{consumer.rollbackToBuiltinDefault ? copy.detail.rollbackBuiltinSuffix : ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function PromptArtifactLifecycleActionCard({
  preview,
  actionLabel,
  copy,
  onPreview,
}: {
  preview: PromptMutationPreview;
  actionLabel: string;
  copy: PromptControlPlaneCopy;
  onPreview: () => void;
}) {
  return (
    <div className="record-card">
      <strong>{preview.title}</strong>
      <p>{preview.evidence}</p>
      {preview.available ? (
        <button className="btn btn-primary" type="button" onClick={onPreview}>{actionLabel}</button>
      ) : (
        <span className="pill">{copy.previews.unavailableContractGap} {preview.contractGap || preview.evidence}</span>
      )}
    </div>
  );
}

function ActionSummaryCard({
  control,
  candidateOptions,
  copy,
  onOpen,
}: {
  control: PromptMutationPreview;
  candidateOptions: PromptEvidenceCandidate[];
  copy: PromptControlPlaneCopy;
  onOpen: () => void;
}) {
  return (
    <div className="record-card">
      <strong>{control.title}</strong>
      <p>{control.evidence}</p>
      {candidateOptions.length ? <p className="muted-inline">{copy.previews.selected(candidateOptions.length)}</p> : null}
      {control.available ? (
        <button className="btn btn-primary" type="button" onClick={onOpen}>{copy.detail.openActionDrawer}</button>
      ) : (
        <button className="btn" type="button" onClick={onOpen}>{copy.previews.unavailableContractGap}</button>
      )}
    </div>
  );
}

function AssignmentTable({ modules, copy }: { modules: PromptAssignmentModel['modules']; copy: PromptControlPlaneCopy }) {
  if (!modules.length) {
    return <div className="empty-state"><strong>{copy.assignments.table.emptyTitle}</strong><p>{copy.assignments.table.emptyDescription}</p></div>;
  }
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>{copy.assignments.table.module}</th>
          <th>{copy.assignments.table.origin}</th>
          <th>{copy.assignments.table.enabled}</th>
          <th>{copy.assignments.table.assignmentState}</th>
          <th>{copy.assignments.table.evidence}</th>
        </tr>
      </thead>
      <tbody>
        {modules.map(module => (
          <tr key={`${module.name}:${module.assignmentSource}:${module.version}`}>
            <td><strong>{module.displayName}</strong><br /><small>{module.name}</small></td>
            <td>{module.origin} · {module.scope}</td>
            <td>{module.enabled ? copy.common.enabled : copy.common.disabled}<br /><small>{module.configurable ? copy.common.configurable : copy.common.locked}</small></td>
            <td>{module.inherited ? copy.assignments.table.inheritedBaseline : module.platformOverride ? copy.assignments.table.platformOverride : copy.assignments.table.baseline}<br /><small>{module.assignmentSource}</small></td>
            <td>{module.evidence}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ActionCard({
  control,
  candidateOptions,
  confirm,
  selectedKeys,
  copy,
  onConfirm,
  onRun,
  onToggleCandidate,
}: {
  control: PromptMutationPreview;
  candidateOptions: PromptEvidenceCandidate[];
  confirm: boolean;
  selectedKeys: string[];
  copy: PromptControlPlaneCopy;
  onConfirm: (checked: boolean) => void;
  onRun: () => void;
  onToggleCandidate: (candidate: PromptEvidenceCandidate, checked: boolean) => void;
}) {
  return (
    <div className="record-card">
      <strong>{control.title}</strong>
      <p>{control.evidence}</p>
      {candidateOptions.length ? (
        <div className="candidate-list">
          {candidateOptions.map(candidate => (
            <label className="confirm-row" key={candidate.key}>
              <input type="checkbox" checked={selectedKeys.includes(candidate.key)} onChange={event => onToggleCandidate(candidate, event.target.checked)} />
              {copy.previews.candidateCurrent(candidate.personaName, candidate.platform || copy.common.defaultScope, candidate.currentReferencedVersion || copy.common.builtin)}
            </label>
          ))}
        </div>
      ) : null}
      <RequestPreview preview={control} copy={copy} />
      {control.available ? (
        <>
          <label className="confirm-row">
            <input type="checkbox" checked={confirm} onChange={event => onConfirm(event.target.checked)} />
            {copy.confirmations.reviewExactRequest}
          </label>
          <button className="btn btn-primary" type="button" disabled={!confirm} onClick={onRun}>{copy.detail.actions.apply}</button>
        </>
      ) : (
        <span className="pill">{copy.previews.unavailableContractGap} {control.contractGap || control.evidence}</span>
      )}
    </div>
  );
}

function MutationPreviewPanel({
  preview,
  confirmed,
  copy,
  onConfirm,
  onRun,
}: {
  preview: PromptMutationPreview | null;
  confirmed: boolean;
  copy: PromptControlPlaneCopy;
  onConfirm: (checked: boolean) => void;
  onRun: () => void;
}) {
  if (!preview) {
    return <div className="empty-state"><strong>{copy.assignments.edit.noPreviewTitle}</strong><p>{copy.assignments.edit.noPreviewDescription}</p></div>;
  }
  return (
    <div className="record-card">
      <strong>{preview.title}</strong>
      <p>{preview.evidence}</p>
      <RequestPreview preview={preview} copy={copy} />
      {preview.available ? (
        <>
          <label className="confirm-row">
            <input type="checkbox" checked={confirmed} onChange={event => onConfirm(event.target.checked)} />
            {copy.confirmations.reviewAssignmentScope}
          </label>
          <button className="btn btn-primary" type="button" disabled={!confirmed} onClick={onRun}>{copy.assignments.edit.saveDraft}</button>
        </>
      ) : (
        <div className="notice notice-warning"><strong>{copy.previews.unavailableContractGap}</strong> {preview.contractGap || preview.evidence}</div>
      )}
    </div>
  );
}

function ArtifactLifecyclePreviewPanel({
  preview,
  confirmed,
  copy,
  onConfirm,
  onRun,
}: {
  preview: PromptMutationPreview | null;
  confirmed: boolean;
  copy: PromptControlPlaneCopy;
  onConfirm: (checked: boolean) => void;
  onRun: () => void;
}) {
  if (!preview) {
    return <div className="empty-state"><strong>{copy.assignments.edit.noPreviewTitle}</strong><p>{copy.detail.editorDescription}</p></div>;
  }
  return (
    <div className="record-card">
      <strong>{preview.title}</strong>
      <p>{preview.evidence}</p>
      <RequestPreview preview={preview} copy={copy} />
      {preview.available ? (
        <>
          <label className="confirm-row">
            <input type="checkbox" checked={confirmed} onChange={event => onConfirm(event.target.checked)} />
            {copy.confirmations.reviewExactRequest}
          </label>
          <button className="btn btn-primary" type="button" disabled={!confirmed} onClick={onRun}>{copy.detail.actions.apply}</button>
        </>
      ) : (
        <div className="notice notice-warning"><strong>{copy.previews.unavailableContractGap}</strong> {preview.contractGap || preview.evidence}</div>
      )}
    </div>
  );
}

function RequestPreview({ preview, copy }: { preview: PromptMutationPreview; copy: PromptControlPlaneCopy }) {
  return (
    <div className="mutation-preview">
      <dl className="record-meta">
        <div><dt>{copy.previews.method}</dt><dd>{preview.method}</dd></div>
        <div><dt>{copy.previews.endpoint}</dt><dd>{preview.endpoint}</dd></div>
      </dl>
      <pre>{preview.bodyJson}</pre>
    </div>
  );
}

function UnavailableNotice({ errors, loading, copy }: { errors: Record<string, string>; loading: boolean; copy: PromptControlPlaneCopy }) {
  const ui = useVisibleCopy();
  const entries = Object.entries(errors).filter(([, value]) => value);
  if (!entries.length) return loading ? <div className="notice">{copy.common.loadingLiveData}</div> : null;
  return (
    <div className="notice notice-warning">
      <strong>{copy.common.partialUnavailable}</strong>
      <ul>
        {entries.map(([key, value]) => <li key={key}>{ui(`${key}: ${value}`)}</li>)}
      </ul>
    </div>
  );
}

async function capture(key: string, promise: Promise<unknown>, errors: Record<string, string>) {
  try {
    return await promise;
  } catch (error) {
    errors[key] = error instanceof Error ? error.message : String(error);
    return undefined;
  }
}

function normalizeDetailTab(value: string | null | undefined): DetailTab {
  return value === 'evidence' || value === 'history' ? value : 'overview';
}

function normalizeDetailDrawer(value: string | null | undefined): DetailDrawer {
  if (value === 'edit' || value === 'draft_json' || value === 'config_json') return value;
  if (value === 'save_draft' || value === 'archive' || value === 'delete') return value;
  return isPromptModuleActionKind(value) ? value : null;
}

function isPromptModuleActionKind(value: unknown): value is PromptModuleActionKind {
  return value === 'create_override'
    || value === 'create_version'
    || value === 'publish'
    || value === 'rollout_all'
    || value === 'rollout_selected'
    || value === 'rollback'
    || value === 'rollback_builtin_default'
    || value === 'destructive';
}

function normalizeAssignmentTab(value: string | null | undefined): AssignmentTab {
  return value === 'modules' || value === 'edit' || value === 'raw' ? value : 'overview';
}

function normalizePlatform(value: string | null | undefined) {
  if (!value) return '';
  const normalized = value.trim().toLowerCase();
  if (!isRealPersonaPlatform(normalized)) return '';
  return normalized;
}

function promptEditorFormFromModel(model: PromptModuleDetailModel): PromptEditorFormState {
  return {
    displayName: model.editor.displayName,
    description: model.editor.description,
    markdown: model.editor.markdown,
    priority: model.editor.priority || 10,
    conditions: {
      platforms: [...model.editor.conditions.platforms],
      agents: [...model.editor.conditions.agents],
    },
    status: model.editor.status,
    classification: model.editor.classification,
    scope: model.editor.scope,
    overrideTarget: model.editor.overrideTarget,
  };
}

function promptDraftInputFromEditorForm(form: PromptEditorFormState, userEditedMarkdown: boolean) {
  return {
    markdown: form.markdown,
    priority: form.priority,
    displayName: form.displayName,
    description: form.description,
    conditions: form.conditions,
    userEditedMarkdown,
  };
}

function promptAgentConditionOptions(model: PromptModuleDetailModel) {
  return Array.from(new Set(model.consumers.map(consumer => consumer.personaName || consumer.personaId).filter(Boolean)));
}

function csvToList(value: string) {
  return Array.from(new Set(value.split(',').map(item => item.trim()).filter(Boolean)));
}

function actionCandidates(model: PromptModuleDetailModel, action: PromptModuleActionKind) {
  if (action === 'rollout_selected') return model.evidence.selectiveRollout.candidates;
  if (action === 'rollback') return model.evidence.rollbackConsumers.candidates.filter(candidate => !candidate.rollbackToBuiltinDefault);
  if (action === 'rollback_builtin_default') return model.evidence.rollbackToBuiltinDefault.candidates;
  return [];
}

function createdOverrideId(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
  const record = value as Record<string, unknown>;
  const manifest = record.manifest;
  if (!manifest || typeof manifest !== 'object' || Array.isArray(manifest)) return '';
  const id = (manifest as Record<string, unknown>).id;
  return typeof id === 'string' ? id : '';
}

function createdPromptModuleId(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
  const record = value as Record<string, unknown>;
  const direct = record.id;
  if (typeof direct === 'string') return direct;
  const manifest = record.manifest;
  if (manifest && typeof manifest === 'object' && !Array.isArray(manifest)) {
    const id = (manifest as Record<string, unknown>).id;
    if (typeof id === 'string') return id;
  }
  return '';
}
